import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import { Brand } from "../components/NavBar";
import { getAnswers, setAnswers, markCompleted } from "../lib/onboarding";
import { saveOnboarding, track } from "../lib/api";

// One question per screen. `multi` allows multiple selections. `optional` can be skipped.
const STEPS = [
  { key: "first_name", type: "text", title: "What should we call you?", placeholder: "Your first name", hint: "Just a first name or nickname is fine." },
  { key: "other_name", type: "text", title: "Who are you trying to understand?", placeholder: "Their name or nickname", hint: "This is only used to personalize your experience." },
  {
    key: "relationship", type: "single", title: "What is your relationship?",
    options: [
      ["partner", "Partner or spouse"], ["ex", "Ex-partner"], ["dating", "Dating"],
      ["family", "Family member"], ["friend", "Friend"], ["coworker", "Coworker"], ["other", "Other"],
    ],
  },
  {
    key: "behaviors", type: "multi", title: "What behaviors concern you?", hint: "Select all that apply.",
    options: [
      ["memory", "Making you question your memory"], ["blame", "Blaming you"],
      ["accountability", "Avoiding accountability"], ["guilt", "Guilt around boundaries"],
      ["withdrawal", "Affection followed by withdrawal"], ["dismiss", "Dismissing your feelings"],
      ["control", "Controlling behavior"], ["off", "Something feels off"],
    ],
  },
  {
    key: "frequency", type: "single", title: "How often does this happen?",
    options: [["rarely", "Rarely"], ["sometimes", "Sometimes"], ["often", "Often"], ["most", "Almost every time we disagree"], ["unsure", "Unsure"]],
  },
  {
    key: "feelings", type: "multi", title: "How does the relationship make you feel?", hint: "Select all that apply.",
    options: [
      ["confused", "Confused"], ["anxious", "Anxious"], ["guilty", "Guilty"], ["drained", "Drained"],
      ["eggshells", "Walking on eggshells"], ["respected", "Heard and respected"], ["unsure", "Unsure"],
    ],
  },
  {
    key: "help_with", type: "single", title: "What would you like help with?",
    options: [
      ["screenshots", "Screenshot analysis"], ["confusing", "Understanding confusing interactions"],
      ["patterns", "Exploring possible manipulation patterns"], ["communication", "Understanding communication"],
      ["respond", "Deciding how to respond"], ["boundaries", "Setting boundaries"],
    ],
  },
  {
    key: "duration", type: "single", title: "How long has this been happening?",
    options: [["lt_month", "Less than a month"], ["1_6", "1–6 months"], ["6_12", "6–12 months"], ["gt_year", "More than a year"], ["comes_goes", "Comes and goes"]],
  },
  { key: "biggest_question", type: "text", title: "What is your biggest question about this situation?", placeholder: "Optional — type a short note", optional: true, multiline: true },
];

export default function OnboardingFlow() {
  const navigate = useNavigate();
  const [answers, setLocal] = useState(() => getAnswers());
  const [i, setI] = useState(0);
  const step = STEPS[i];
  const pct = Math.round(((i + 1) / STEPS.length) * 100);

  const value = answers[step.key];

  const persist = (next) => {
    setLocal(next);
    setAnswers(next);
  };

  const setValue = (v) => persist({ ...answers, [step.key]: v });

  const toggleMulti = (optKey) => {
    const cur = Array.isArray(value) ? value : [];
    const next = cur.includes(optKey) ? cur.filter((x) => x !== optKey) : [...cur, optKey];
    setValue(next);
  };

  const canNext = useMemo(() => {
    if (step.optional) return true;
    if (step.type === "multi") return Array.isArray(value) && value.length > 0;
    return typeof value === "string" ? value.trim().length > 0 : !!value;
  }, [step, value]);

  const back = () => {
    if (i === 0) navigate("/");
    else setI(i - 1);
  };

  const next = async () => {
    if (!canNext) return;
    if (i < STEPS.length - 1) {
      setI(i + 1);
      return;
    }
    // Finished.
    markCompleted();
    saveOnboarding(answers, true).catch(() => {});
    track("onboarding_completed");
    navigate("/transition");
  };

  return (
    <>
      <header className="nav">
        <div className="container-nc nav-inner">
          <Brand onClick={() => navigate("/")} />
          <span className="mono" style={{ color: "var(--muted)", fontSize: 12 }} data-testid="onboarding-step-count">
            {i + 1} / {STEPS.length}
          </span>
        </div>
      </header>

      {/* Progress bar */}
      <div style={{ height: 4, background: "var(--surface-4)", width: "100%" }}>
        <div
          data-testid="onboarding-progress"
          style={{ height: "100%", width: `${pct}%`, background: "var(--brand)", transition: "width 0.3s ease" }}
        />
      </div>

      <main className="container-nc" style={{ paddingTop: 40, paddingBottom: 110, maxWidth: 560 }}>
        <div key={step.key} className="fade-up">
          <h1 style={{ fontSize: "clamp(1.7rem, 4.5vw, 2.3rem)", fontWeight: 600, letterSpacing: "-0.02em", lineHeight: 1.15 }} data-testid="onboarding-question">
            {step.title}
          </h1>
          {step.hint && <p style={{ color: "var(--muted)", fontSize: 14.5, marginTop: 8 }}>{step.hint}</p>}

          <div style={{ marginTop: 26 }}>
            {step.type === "text" && (
              step.multiline ? (
                <textarea
                  className="textarea"
                  placeholder={step.placeholder}
                  value={value || ""}
                  onChange={(e) => setValue(e.target.value)}
                  data-testid="onboarding-input"
                  style={{ minHeight: 120 }}
                  autoFocus
                />
              ) : (
                <input
                  className="input"
                  placeholder={step.placeholder}
                  value={value || ""}
                  onChange={(e) => setValue(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && canNext && next()}
                  data-testid="onboarding-input"
                  autoFocus
                />
              )
            )}

            {(step.type === "single" || step.type === "multi") && (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {step.options.map(([optKey, label]) => {
                  const selected = step.type === "multi"
                    ? Array.isArray(value) && value.includes(optKey)
                    : value === optKey;
                  return (
                    <button
                      key={optKey}
                      data-testid={`onboarding-opt-${optKey}`}
                      onClick={() => (step.type === "multi" ? toggleMulti(optKey) : setValue(optKey))}
                      className="card"
                      style={{
                        display: "flex", alignItems: "center", gap: 12, textAlign: "left",
                        padding: "15px 16px", cursor: "pointer",
                        borderColor: selected ? "var(--brand)" : "var(--border)",
                        background: selected ? "var(--brand-tertiary)" : "var(--surface-2)",
                      }}
                    >
                      <span style={{
                        width: 22, height: 22, borderRadius: step.type === "multi" ? 6 : "50%",
                        border: `1.5px solid ${selected ? "var(--brand)" : "var(--border-strong)"}`,
                        background: selected ? "var(--brand)" : "transparent",
                        display: "grid", placeItems: "center", flexShrink: 0,
                      }}>
                        {selected && <Check size={13} color="#fff" />}
                      </span>
                      <span style={{ fontSize: 15, fontWeight: 500 }}>{label}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Sticky nav */}
      <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: "var(--surface)", borderTop: "1px solid var(--border)", padding: "14px 0" }}>
        <div className="container-nc" style={{ display: "flex", gap: 12, maxWidth: 560 }}>
          <button className="btn btn-ghost" onClick={back} data-testid="onboarding-back" style={{ flex: "0 0 auto" }}>
            <ArrowLeft size={16} /> Back
          </button>
          <button className="btn btn-primary btn-block" onClick={next} disabled={!canNext} data-testid="onboarding-next" style={{ flex: 1 }}>
            {i < STEPS.length - 1 ? (<>Continue <ArrowRight size={16} /></>) : (<>See my summary <ArrowRight size={16} /></>)}
          </button>
        </div>
      </div>
    </>
  );
}
